import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { Observable } from 'rxjs';

export type ZoneMakerZoneType = 'customer' | 'carrier';
export type ZoneMakerBoundaryCountry = 'USA' | 'CAN';

export interface ZoneMakerApiResponse<T> {
  file: string;
  success: boolean;
  code: string;
  message: string;
  data: T;
}

export type ZoneMakerFeatureCollection = FeatureCollection<Geometry, GeoJsonProperties>;

export interface ZoneMakerBoundsRequest {
  west: number;
  south: number;
  east: number;
  north: number;
  zoom: number;
  states: string[];
  country: ZoneMakerBoundaryCountry;
}

export interface ZoneMakerStateOption {
  state_abbr: string;
  state_name: string;
  zip_count: string | number;
}

export interface ZoneMakerZipsResponse {
  count: number;
  limit: number;
  feature_collection: ZoneMakerFeatureCollection;
}

export interface ZoneMakerZipSearchResult {
  postal_code: string;
  state_abbr: string;
  state_name: string;
  center_lat: string | number;
  center_lng: string | number;
  country_code?: string;
  boundary_type?: string;
}

export interface ZoneMakerZipResponse {
  postal_code: string;
  state_abbr: string;
  state_name: string;
  center_lat: number;
  center_lng: number;
  country: ZoneMakerBoundaryCountry;
  bbox_geometry: Geometry | null;
  feature_collection: ZoneMakerFeatureCollection;
}

export interface ZoneMakerZoneSummary {
  zone_uuid: string;
  zone_name: string;
  zone_id: string | null;
  country: string;
  active: boolean;
  postal_code_count: number;
  center_lat: number | null;
  center_lng: number | null;
}

export interface ZoneMakerZonesResponse {
  zones: ZoneMakerZoneSummary[];
  feature_collection: ZoneMakerFeatureCollection;
}

export interface ZoneMakerZoneDetail {
  zone_uuid: string;
  zone_name: string;
  zone_id: string | null;
  country: string;
  active: boolean;
  description: string | null;
  center_lat: number | null;
  center_lng: number | null;
  postal_codes: string[];
  region_codes?: string[];
}

export interface ZoneMakerZoneResponse {
  zone: ZoneMakerZoneDetail;
  feature_collection: ZoneMakerFeatureCollection;
}

export interface ZoneMakerRadiusRequest {
  radius_miles: number;
  zoom: number;
  states: string[];
  country: ZoneMakerBoundaryCountry;
  center_postal_code?: string;
  lat?: number;
  lng?: number;
}

export interface ZoneMakerRadiusResponse {
  postal_codes: string[];
  count: number;
  limit: number;
  radius_miles: number;
  center: { lat: number; lng: number } | null;
  feature_collection: ZoneMakerFeatureCollection;
}

export interface ZoneMakerPreviewResponse {
  country: ZoneMakerBoundaryCountry;
  gap_fill_miles: number;
  clean_hole_square_miles?: number;
  postal_code_count: number;
  square_miles: number | null;
  center_lat: number | null;
  center_lng: number | null;
  feature_collection: ZoneMakerFeatureCollection;
}

export interface ZoneMakerSaveRequest {
  zone_type: ZoneMakerZoneType;
  contact_uuid: string;
  zone_uuid?: string;
  zone_name: string;
  zone_id: string;
  country: string;
  description: string;
  postal_codes: string[];
  gap_fill_miles: number;
  clean_hole_square_miles?: number;
}

export interface ZoneMakerSaveResponse {
  zone_uuid: string;
  zone_type: ZoneMakerZoneType;
  country: ZoneMakerBoundaryCountry;
  postal_code_count: number;
}

@Injectable({
  providedIn: 'root'
})
export class ZoneMakerApiService {
  private readonly apiUrl = '/api/zone_maker.php';

  constructor(private readonly http: HttpClient) {}

  getStates(country: ZoneMakerBoundaryCountry): Observable<ZoneMakerApiResponse<{ country: ZoneMakerBoundaryCountry; states: ZoneMakerStateOption[] }>> {
    const params = new HttpParams()
      .set('action', 'states')
      .set('country', country);

    return this.http.get<ZoneMakerApiResponse<{ country: ZoneMakerBoundaryCountry; states: ZoneMakerStateOption[] }>>(this.apiUrl, { params });
  }

  getZips(bounds: ZoneMakerBoundsRequest): Observable<ZoneMakerApiResponse<ZoneMakerZipsResponse>> {
    const params = new HttpParams()
      .set('action', 'zips')
      .set('west', String(bounds.west))
      .set('south', String(bounds.south))
      .set('east', String(bounds.east))
      .set('north', String(bounds.north))
      .set('zoom', String(bounds.zoom))
      .set('country', bounds.country)
      .set('states', bounds.states.join(','));

    return this.http.get<ZoneMakerApiResponse<ZoneMakerZipsResponse>>(this.apiUrl, { params });
  }

  getZip(postalCode: string, zoom: number, country: ZoneMakerBoundaryCountry): Observable<ZoneMakerApiResponse<ZoneMakerZipResponse>> {
    const params = new HttpParams()
      .set('action', 'zip')
      .set('postal_code', postalCode)
      .set('country', country)
      .set('zoom', String(zoom));

    return this.http.get<ZoneMakerApiResponse<ZoneMakerZipResponse>>(this.apiUrl, { params });
  }

  searchZips(query: string, country: ZoneMakerBoundaryCountry): Observable<ZoneMakerApiResponse<{ country: ZoneMakerBoundaryCountry; results: ZoneMakerZipSearchResult[] }>> {
    const params = new HttpParams()
      .set('action', 'zip_search')
      .set('query', query)
      .set('country', country);

    return this.http.get<ZoneMakerApiResponse<{ country: ZoneMakerBoundaryCountry; results: ZoneMakerZipSearchResult[] }>>(this.apiUrl, { params });
  }

  getZones(zoneType: ZoneMakerZoneType, contactUuid: string, zoom: number): Observable<ZoneMakerApiResponse<ZoneMakerZonesResponse>> {
    const params = new HttpParams()
      .set('action', 'zones')
      .set('zone_type', zoneType)
      .set('contact_uuid', contactUuid)
      .set('zoom', String(zoom));

    return this.http.get<ZoneMakerApiResponse<ZoneMakerZonesResponse>>(this.apiUrl, { params });
  }

  getZone(zoneType: ZoneMakerZoneType, contactUuid: string, zoneUuid: string, zoom: number): Observable<ZoneMakerApiResponse<ZoneMakerZoneResponse>> {
    const params = new HttpParams()
      .set('action', 'zone')
      .set('zone_type', zoneType)
      .set('contact_uuid', contactUuid)
      .set('zone_uuid', zoneUuid)
      .set('zoom', String(zoom));

    return this.http.get<ZoneMakerApiResponse<ZoneMakerZoneResponse>>(this.apiUrl, { params });
  }

  radiusSelect(request: ZoneMakerRadiusRequest): Observable<ZoneMakerApiResponse<ZoneMakerRadiusResponse>> {
    return this.http.post<ZoneMakerApiResponse<ZoneMakerRadiusResponse>>(this.apiUrl, {
      action: 'radius_select',
      ...request
    });
  }

  preview(postalCodes: string[], zoom: number, country: ZoneMakerBoundaryCountry, gapFillMiles: number, cleanHoleSquareMiles: number): Observable<ZoneMakerApiResponse<ZoneMakerPreviewResponse>> {
    return this.http.post<ZoneMakerApiResponse<ZoneMakerPreviewResponse>>(this.apiUrl, {
      action: 'preview',
      postal_codes: postalCodes,
      country,
      zoom,
      gap_fill_miles: gapFillMiles,
      clean_hole_square_miles: cleanHoleSquareMiles
    });
  }

  save(request: ZoneMakerSaveRequest): Observable<ZoneMakerApiResponse<ZoneMakerSaveResponse>> {
    return this.http.post<ZoneMakerApiResponse<ZoneMakerSaveResponse>>(this.apiUrl, {
      action: 'save',
      ...request
    });
  }
}
